/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { useIsMutating } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import {
    Badge,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    MoreButton,
    Pagination,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useDebounce } from "../../../../hooks/helpers";
import { useDoesUserHavePermission } from "../../../../hooks/permissions";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
} from "../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import { UserPermisions } from "../../../../utils/types/user";
import { useFetchProviderAccommodations } from "../../hooks/queries/reports.queries";
import {
    IProviderAccommodation,
    ProviderAccommodationStatus,
} from "../../types/reports.types";
import { getAccommodationNoteCategoryLabel } from "../../helpers/reports.helper";
import ProviderAccommodationForm from "../ProviderAccommodationForm/ProviderAccommodationForm";
import DeleteProviderAccommodationForm from "../DeleteProviderAccommodationForm/DeleteProviderAccommodationForm";

type DialogKind =
    | { kind: "form"; accommodation?: IProviderAccommodation }
    | { kind: "delete"; accommodation: IProviderAccommodation }
    | null;

const getAccommodationStatusTag = (status: ProviderAccommodationStatus) => {
    const content = makeStringFirstLetterCapital(status);

    if (status === ProviderAccommodationStatus.ACTIVE) {
        return <Badge type="success" content={content} />;
    }
    if (status === ProviderAccommodationStatus.UPCOMING) {
        return <Badge type="info" content={content} />;
    }
    return <Badge type="neutral" content={content} />;
};

export default function ProviderAccommodationsList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<DialogKind>(null);

    const canWriteAccommodations = useDoesUserHavePermission([
        UserPermisions.PROVIDER_ACCOMMODATION_WRITE,
    ]);

    const saveIsMutating =
        useIsMutating(["create-provider-accommodation"]) +
        useIsMutating(["update-provider-accommodation"]) +
        useIsMutating(["delete-provider-accommodation"]);

    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const providerNameFilter = searchParams.get("provider_name") || "";

    const debouncedProviderName = useDebounce(providerNameFilter);

    const { data, error, isLoading, isSuccess } =
        useFetchProviderAccommodations({
            provider_name: debouncedProviderName || undefined,
            page: pageFilter,
            limit: limitFilter,
        });

    const handleProviderNameChange = (value: string) => {
        if (!value) searchParams.delete("provider_name");
        else searchParams.set("provider_name", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const getDialogTitle = () => {
        if (dialogInView?.kind === "delete") return "Delete accommodation";
        return dialogInView?.accommodation
            ? "Edit accommodation"
            : "Add accommodation";
    };

    const columns: ColumnDef<IProviderAccommodation>[] = [
        {
            accessorKey: "provider_name",
            header: "PROVIDER",
            cell: ({ row }) =>
                displayNameInRightFormat(row.original.provider_name),
        },
        {
            accessorKey: "window_hours",
            header: "WINDOW",
            cell: ({ row }) => (
                <Badge type="info" content={`${row.original.window_hours}h`} />
            ),
        },
        {
            accessorKey: "note_categories",
            header: "APPLIES TO",
            cell: ({ row }) =>
                row.original.note_categories
                    .map(getAccommodationNoteCategoryLabel)
                    .join(", "),
        },
        {
            accessorKey: "effective_from",
            header: "EFFECTIVE",
            cell: ({ row }) =>
                `${formatDate(row.original.effective_from, true)} - ${
                    row.original.effective_to
                        ? formatDate(row.original.effective_to, true)
                        : "Ongoing"
                }`,
        },
        {
            accessorKey: "status",
            header: "STATUS",
            cell: ({ row }) => getAccommodationStatusTag(row.original.status),
        },
        {
            accessorKey: "notes",
            header: "NOTES",
            cell: ({ row }) => (
                <span className="whitespace-pre-wrap">
                    {row.original.notes || "--"}
                </span>
            ),
        },
        {
            accessorKey: "created_by",
            header: "SET BY",
            cell: ({ row }) =>
                row.original.created_by.first_name
                    ? displayNameInRightFormat({
                          firstName: row.original.created_by.first_name,
                          lastName: row.original.created_by.last_name,
                      })
                    : "--",
        },
        {
            id: "actions",
            header: "",
            cell: ({ row }) =>
                canWriteAccommodations ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            <DropdownItem
                                onClick={() =>
                                    setDialogInView({
                                        kind: "form",
                                        accommodation: row.original,
                                    })
                                }
                            >
                                Edit
                            </DropdownItem>
                            <DropdownItem
                                onClick={() =>
                                    setDialogInView({
                                        kind: "delete",
                                        accommodation: row.original,
                                    })
                                }
                            >
                                Delete
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown>
                ) : null,
        },
    ];

    return (
        <>
            {dialogInView && (
                <Dialog open>
                    <DialogContent
                        variant="center"
                        handleCloseDialog={() => setDialogInView(null)}
                        title={getDialogTitle()}
                        showFooter
                        saveText={
                            dialogInView.kind === "delete"
                                ? "Delete"
                                : "Save accommodation"
                        }
                        cancelText="Cancel"
                        isDeleting={dialogInView.kind === "delete"}
                        submitBtnFormValue={
                            dialogInView.kind === "delete"
                                ? "delete-provider-accommodation"
                                : "provider-accommodation-form"
                        }
                        isCancelBtnDisabled={saveIsMutating > 0}
                        isSubmitBtnDisabled={saveIsMutating > 0}
                    >
                        {dialogInView.kind === "delete" ? (
                            <DeleteProviderAccommodationForm
                                accommodation={dialogInView.accommodation}
                                onFormSubmit={() => setDialogInView(null)}
                            />
                        ) : (
                            <ProviderAccommodationForm
                                accommodation={dialogInView.accommodation}
                                onFormSubmit={() => setDialogInView(null)}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}

            <Card classNames="mb-[--spacing-header]">
                <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                    <SearchInput
                        defaultValue={providerNameFilter}
                        onChange={(e) =>
                            handleProviderNameChange(e.target.value)
                        }
                        containerClass="w-[400px]"
                        placeholder="Search by provider name"
                    />
                    {canWriteAccommodations && (
                        <Button
                            onClick={() => setDialogInView({ kind: "form" })}
                        >
                            Add accommodation
                        </Button>
                    )}
                </div>
            </Card>

            {isLoading ? <Loader /> : null}

            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display accommodations at this time"
                    }
                />
            )}

            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    isError={false}
                    stateHelperText="Accommodations will appear here once added"
                    emptyMessage="No accommodations added yet"
                />
            )}

            {data && isSuccess && data.data.length > 0 && (
                <Table
                    columns={columns}
                    data={data.data}
                    hasPagination={data.total_count > 20}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            limit={limitFilter}
                            onLimitChange={handleLimitChange}
                        />
                    }
                />
            )}
        </>
    );
}
